import { create } from 'zustand';
import type { Category } from '@/domain/model/Category';
import type { CreateCategoryRequest } from '@/application/dto/CreateCategoryRequest';
import type { UpdateCategoryRequest } from '@/application/dto/UpdateCategoryRequest';
import { adminService } from '@/infrastructure/store/adminServiceInstance';
import { resolveErrorKey } from '@/infrastructure/util/error';

interface AdminCategoryState {
  categories: Category[];
  isLoading: boolean;
  error: string | null;
}

interface AdminCategoryActions {
  fetchCategories: () => Promise<void>;
  createCategory: (data: CreateCategoryRequest) => Promise<void>;
  updateCategory: (id: string, data: UpdateCategoryRequest) => Promise<void>;
  deleteCategory: (id: string) => Promise<boolean>;
  clearError: () => void;
}

export const useAdminCategoryStore = create<AdminCategoryState & AdminCategoryActions>(
  (set, get) => ({
    categories: [],
    isLoading: false,
    error: null,

    fetchCategories: async () => {
      set({ isLoading: true, error: null });
      try {
        const categories = await adminService.getCategories();
        set({ categories, isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: resolveErrorKey(e, 'admin.categoryLoadFailed') });
      }
    },

    createCategory: async (data: CreateCategoryRequest) => {
      set({ isLoading: true, error: null });
      try {
        const category = await adminService.createCategory(data);
        set({ categories: [...get().categories, category], isLoading: false });
      } catch (e) {
        set({ isLoading: false, error: resolveErrorKey(e, 'admin.categorySaveFailed') });
      }
    },

    updateCategory: async (id: string, data: UpdateCategoryRequest) => {
      set({ isLoading: true, error: null });
      try {
        const updated = await adminService.updateCategory(id, data);
        set({
          categories: get().categories.map((c) => (c.id === id ? updated : c)),
          isLoading: false,
        });
      } catch (e) {
        set({ isLoading: false, error: resolveErrorKey(e, 'admin.categorySaveFailed') });
      }
    },

    deleteCategory: async (id: string) => {
      set({ isLoading: true, error: null });
      try {
        await adminService.deleteCategory(id);
        set({ isLoading: false });
        return true;
      } catch {
        set({ isLoading: false });
        return false;
      }
    },

    clearError: () => set({ error: null }),
  }),
);
